/**
 * Custom hook for Maintenance state management
 * Extraído de App.js para mejor mantenibilidad
 */
import { useState, useCallback, useEffect } from "react";
import api from "../lib/axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function useMaintenanceState(activeView, factoryPassword) {
  // Maintenance types state
  const [maintenanceTypes, setMaintenanceTypes] = useState([]);
  const [maintenanceSearchQuery, setMaintenanceSearchQuery] = useState("");
  
  // Merge state
  const [selectedMergeSource, setSelectedMergeSource] = useState(null);
  const [selectedMergeTarget, setSelectedMergeTarget] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  
  // Similar types state
  const [similarTypes, setSimilarTypes] = useState([]);
  const [isLoadingSimilar, setIsLoadingSimilar] = useState(false);
  
  // Project management states
  const [maintenanceProjects, setMaintenanceProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectAdjustPercent, setProjectAdjustPercent] = useState("");
  const [isAdjustingProject, setIsAdjustingProject] = useState(false);
  
  // Callback for external refreshes (set by parent component)
  const [onDataRefresh, setOnDataRefresh] = useState(null);

  // Fetch maintenance types
  const fetchMaintenanceTypes = useCallback(async () => {
    try {
      const response = await api.get(`${API}/maintenance/types`);
      setMaintenanceTypes(response.data);
    } catch (error) {
      console.error("Error fetching maintenance types:", error?.message || error);
    }
  }, []);

  // Fetch projects for maintenance
  const fetchMaintenanceProjects = useCallback(async () => {
    try {
      const response = await api.get(`${API}/maintenance/projects`);
      setMaintenanceProjects(response.data.projects || []);
    } catch (error) {
      console.error("Error fetching maintenance projects:", error?.message || error);
    }
  }, []);

  // Adjust project costs by percentage
  const handleAdjustProjectCosts = useCallback(async (refreshCallbacks) => {
    if (!selectedProject) {
      toast.error('Selecciona un proyecto');
      return;
    }
    
    const percent = parseFloat(projectAdjustPercent);
    if (isNaN(percent)) {
      toast.error('Ingresa un porcentaje válido');
      return;
    }
    
    if (!window.confirm(`¿Aplicar ajuste de ${percent > 0 ? '+' : ''}${percent}% a todos los costos del proyecto "${selectedProject.project_name}"?`)) {
      return;
    }
    
    setIsAdjustingProject(true);
    try {
      const response = await api.post(`${API}/maintenance/adjust-project-costs`, {
        project_name: selectedProject.project_name,
        new_percentage: percent,
        password: factoryPassword
      });
      
      toast.success(response.data.message);
      setProjectAdjustPercent("");
      fetchMaintenanceProjects();
      // Call external refresh if provided
      if (refreshCallbacks?.fetchFactoryData) refreshCallbacks.fetchFactoryData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ajustar costos');
    } finally {
      setIsAdjustingProject(false);
    }
  }, [selectedProject, projectAdjustPercent, factoryPassword, fetchMaintenanceProjects]);

  // Merge types
  const handleMergeTypes = useCallback(async (refreshCallbacks) => {
    if (!selectedMergeSource || !selectedMergeTarget) {
      toast.error('Selecciona tipo origen y destino');
      return;
    }
    
    if (selectedMergeSource.id === selectedMergeTarget.id) {
      toast.error('No puedes fusionar un tipo consigo mismo');
      return;
    }
    
    if (!window.confirm(`¿Fusionar "${selectedMergeSource.name}" en "${selectedMergeTarget.name}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      const response = await api.put(
        `${API}/maintenance/merge-types?source_id=${selectedMergeSource.id}&target_id=${selectedMergeTarget.id}`
      );
      toast.success(response.data.message);
      setSelectedMergeSource(null);
      setSelectedMergeTarget(null);
      fetchMaintenanceTypes();
      // Call external refresh if provided
      if (refreshCallbacks?.fetchAllData) refreshCallbacks.fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al fusionar tipos');
    }
  }, [selectedMergeSource, selectedMergeTarget, fetchMaintenanceTypes]);

  // Rename type
  const handleRenameType = useCallback(async (typeId, newName, refreshCallbacks) => {
    if (!newName.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    
    try {
      await api.put(`${API}/maintenance/rename-type/${typeId}?new_name=${encodeURIComponent(newName)}`);
      toast.success('Tipo renombrado');
      fetchMaintenanceTypes();
      // Call external refresh if provided
      if (refreshCallbacks?.fetchAllData) refreshCallbacks.fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al renombrar');
    }
  }, [fetchMaintenanceTypes]);

  // Find similar types
  const findSimilarTypes = useCallback(async (typeId) => {
    setIsLoadingSimilar(true);
    setSimilarTypes([]);
    try {
      const response = await api.get(`${API}/maintenance/find-similar?type_id=${typeId}&threshold=22`);
      setSimilarTypes(response.data.similar || []);
      if (response.data.similar?.length === 0) {
        toast.info('No se encontraron tipos similares');
      }
    } catch (error) {
      toast.error('Error al buscar tipos similares');
    } finally {
      setIsLoadingSimilar(false);
    }
  }, []);

  // Clear merge selection
  const clearMergeSelection = useCallback(() => {
    setSelectedMergeSource(null);
    setSelectedMergeTarget(null);
    setSimilarTypes([]);
  }, []);

  // Load maintenance data when view becomes active
  useEffect(() => {
    if (activeView === 'maintenance') {
      fetchMaintenanceTypes();
      fetchMaintenanceProjects();
    }
  }, [activeView, fetchMaintenanceTypes, fetchMaintenanceProjects]);

  return {
    // Types state
    maintenanceTypes,
    setMaintenanceTypes,
    maintenanceSearchQuery,
    setMaintenanceSearchQuery,
    fetchMaintenanceTypes,
    
    // Merge state
    selectedMergeSource,
    setSelectedMergeSource,
    selectedMergeTarget,
    setSelectedMergeTarget,
    compareMode,
    setCompareMode,
    handleMergeTypes,
    
    // Similar types
    similarTypes,
    setSimilarTypes,
    isLoadingSimilar,
    findSimilarTypes,
    
    // Project management
    maintenanceProjects,
    setMaintenanceProjects,
    selectedProject,
    setSelectedProject,
    projectAdjustPercent,
    setProjectAdjustPercent,
    isAdjustingProject,
    handleAdjustProjectCosts,
    fetchMaintenanceProjects,
    
    // Actions
    handleRenameType,
    clearMergeSelection,
    
    // Callback setter for refresh
    setOnDataRefresh
  };
}

export default useMaintenanceState;
